import { formatSnakeStringWithCapitalization } from '../../utils/string_utils'
import { processSingleDateLabelFull } from './time_bucket_processing'
import { useRef } from 'react'
import { StatsDateBucketInterval } from '@/generated/enums'
import { Text } from '@chakra-ui/react'
import { DOPPEL_GREY } from '@/utils/style'
import dynamic from 'next/dynamic'
import {
  dateToStringKey,
  getXAxisTickValues,
  makeLegendData,
  makeTimeLabelMap,
  TimeSeriesChartData,
} from './time_series_utils'
import StatsTooltip from './stats_tooltip'
import { LazyLoadState } from './dashboard/dashboard_utils'

type TimeSeriesLineChartProps<K extends string> = {
  chartData: TimeSeriesChartData<K> | null
  legendKeys: K[]
  bucketInterval: StatsDateBucketInterval
  xAxisTitle?: string
  yAxisLabels?: number[]
  yAxisTitle?: string
  lineColors: string[]
  legendPosition?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left'
  margin?: { top?: number; right?: number; bottom?: number; left?: number }
  setLoading?: (loading: boolean) => void
}

const TimeSeriesLineChart = <K extends string>({
  chartData,
  legendKeys,
  bucketInterval,
  xAxisTitle = null,
  yAxisLabels = undefined,
  yAxisTitle = null,
  lineColors,
  legendPosition = 'bottom-right',
  margin = {},
  setLoading = () => {},
}: TimeSeriesLineChartProps<K>) => {
  const hoveredPointId = useRef<string | null>(null)
  const setHoveredPointId = (pointId: string | null) =>
    (hoveredPointId.current = pointId)

  const timeLabelMap = makeTimeLabelMap(chartData, bucketInterval)

  const ResponsiveLine = dynamic(
    async () => {
      const mod = await import('@nivo/line')
      return mod.ResponsiveLine
    },
    {
      loading: () => <LazyLoadState setLoading={setLoading} />,
    },
  )

  const additionalYAxisLabelProps = yAxisLabels
    ? {
        gridYValues: yAxisLabels,
        maxValue: yAxisLabels[yAxisLabels.length - 1],
      }
    : {}

  if (!chartData) return null

  return (
    <ResponsiveLine
      axisBottom={{
        tickValues: getXAxisTickValues(chartData, bucketInterval).map(
          (date) => new Date(date.valueOf()),
        ),
        tickSize: 5,
        tickPadding: 5,
        tickRotation: 0,

        legend: xAxisTitle,
        legendPosition: 'middle',
        legendOffset: 32,
        format: (value: Date) => {
          return timeLabelMap[dateToStringKey(value, bucketInterval)]
        },
      }}
      axisLeft={{
        tickValues: yAxisLabels,
        tickSize: 2,
        tickPadding: 5,
        tickRotation: 0,
        legend: yAxisTitle,
        legendPosition: 'middle',
        legendOffset: -40,
      }}
      axisRight={null}
      axisTop={null}
      colors={({ id }) => lineColors[legendKeys.indexOf(id as K)]}
      curve="linear"
      data={legendKeys.map((key) => ({
        id: key,
        data: chartData.map((item) => ({
          x: new Date(item.date.valueOf()),
          y: item.values[key],
          date: item.date, // for tooltip access
        })),
      }))}
      enableCrosshair={false}
      enableGridX={false}
      enablePoints={true}
      layers={[
        'grid',
        'markers',
        'axes',
        'areas',
        'crosshair',
        'lines',
        'points',
        ({ points }) => (
          <g>
            {points.map((point) => (
              <circle
                cx={point.x}
                cy={point.y}
                fill={lineColors[legendKeys.indexOf(point.serieId as K)]}
                key={point.id}
                r={point.id === hoveredPointId.current ? 4 : 0}
              />
            ))}
          </g>
        ),
        'slices',
        'mesh',
        'legends',
      ]}
      legends={[
        {
          anchor: legendPosition,
          direction: 'column',
          justify: false,
          translateX: 120,
          translateY: 0,
          itemsSpacing: 2,
          itemWidth: 100,
          itemHeight: 20,
          itemDirection: 'left-to-right',
          itemOpacity: 0.85,
          symbolSize: 20,
          effects: [
            {
              on: 'hover',
              style: {
                itemOpacity: 1,
              },
            },
          ],
          data: makeLegendData(legendKeys, lineColors),
        },
      ]}
      margin={margin}
      onMouseEnter={(point, event) => setHoveredPointId(point.id)}
      onMouseLeave={(point, event) => setHoveredPointId(null)}
      onMouseMove={(point, event) => setHoveredPointId(point.id)}
      theme={{
        text: {
          fill: DOPPEL_GREY,
        },
        grid: {
          line: {
            stroke: DOPPEL_GREY,
            strokeWidth: 0.5, // Set the stroke width of the gridY lines
            strokeOpacity: 0.2,
          },
        },
      }}
      tooltip={({ point, ...props }) => (
        <StatsTooltip>
          {/* type-unsafe access of the date object set in `data prop--
              nivo types don't support keeping extra data there */}

          <Text>{processSingleDateLabelFull(point.data['date'], bucketInterval)}</Text>

          <Text>
            {formatSnakeStringWithCapitalization(String(point.serieId))}:{' '}
            <Text as="b">{point.data.yFormatted}</Text>
          </Text>
        </StatsTooltip>
      )}
      useMesh={true}
      xScale={{
        type: 'time',
        format: '%Y-%m-%d',
        // precision: 'day',
      }}
      yFormat=" >-.2f"
      yScale={{
        type: 'linear',
        min: 0,
        max: 'auto',
        stacked: false,
        reverse: false,
      }}
      {...additionalYAxisLabelProps}
    />
  )
}

export default TimeSeriesLineChart
